import React from "react";
import { Button, notification } from "antd";

export const getSurveyPayoutStorageKey = (formId) =>
  formId ? `survey_${formId}_payout` : null;

export const extractPayoutPayload = (apiResult) => {
  if (!apiResult || typeof apiResult !== "object") {
    return { payout: null, snackbar: null };
  }

  const nestedData =
    apiResult.data && typeof apiResult.data === "object"
      ? apiResult.data
      : null;

  const payout = nestedData?.payout || apiResult.payout || null;

  const snackbar =
    payout?.snackbar || nestedData?.snackbar || apiResult.snackbar || null;

  return { payout, snackbar };
};

export const persistSurveyPayoutState = (formId, payout) => {
  const storageKey = getSurveyPayoutStorageKey(formId);

  if (!storageKey) return;

  try {
    if (!payout) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(storageKey, JSON.stringify(payout));
  } catch {
    // ignore storage failures
  }
};

export const getSurveyPayoutState = (formId) => {
  const storageKey = getSurveyPayoutStorageKey(formId);

  if (!storageKey) return null;

  try {
    const raw = localStorage.getItem(storageKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

export const clearSurveyPayoutState = (formId) => {
  persistSurveyPayoutState(formId, null);
};

const severityToNotificationType = (severity) => {
  switch (String(severity || "").toLowerCase()) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "info":
      return "info";
    case "error":
    default:
      return "error";
  }
};

/**
 * Show payout snackbar from API `{ severity, message }`.
 * Optional retry button when canRetry is true.
 */
export const showPayoutSnackbar = (
  snackbar,
  { canRetry = false, onRetry, isRetrying = false, duration } = {},
) => {
  if (!snackbar?.message) return;

  const type = severityToNotificationType(snackbar.severity);
  const key = `payout-snackbar-${Date.now()}`;
  const shouldOfferRetry =
    canRetry && typeof onRetry === "function" && type === "error";

  notification[type]({
    key,
    message:
      type === "success"
        ? "Reward"
        : type === "warning"
          ? "Reward"
          : type === "info"
            ? "Reward"
            : "Reward payout failed",
    description: String(snackbar.message),
    duration:
      duration ?? (shouldOfferRetry ? 0 : type === "error" ? 10 : 6),
    placement: "topRight",
    style: { whiteSpace: "pre-wrap", maxWidth: 440 },
    btn: shouldOfferRetry
      ? React.createElement(
          Button,
          {
            type: "primary",
            size: "small",
            loading: isRetrying,
            onClick: () => {
              notification.destroy(key);
              onRetry();
            },
          },
          "Retry reward",
        )
      : null,
  });
};

export const applyPayoutFromApiResult = (formId, apiResult, options = {}) => {
  const { payout, snackbar } = extractPayoutPayload(apiResult);

  if (payout) {
    persistSurveyPayoutState(formId, payout);
  }

  if (snackbar) {
    showPayoutSnackbar(snackbar, {
      canRetry: Boolean(payout?.canRetry),
      onRetry: options.onRetry,
      isRetrying: options.isRetrying,
    });
  } else if (payout?.payoutError) {
    showPayoutSnackbar(
      {
        severity: "error",
        message: payout.payoutError,
      },
      {
        canRetry: Boolean(payout?.canRetry),
        onRetry: options.onRetry,
        isRetrying: options.isRetrying,
      },
    );
  }

  return { payout, snackbar };
};
